/* eslint-disable react-refresh/only-export-components */
import { createContext, useEffect, useState, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { UseQueryExecute, useQuery } from 'urql';
import { GetCompaniesForCurrentUserQuery } from '../../gql/graphql.ts';
import { CompaniesQuery } from '../queries/CompaniesQuery.ts';
import { usePublicCompany } from '../../public/hooks/usePublicCompany.ts';
import {
  menu,
  getPrivilegedCurrentMenu,
  getFirstAllowedMenuItem,
  findInMenuTree,
} from '../menu.ts';
import { IAdminMenuItem } from '../AdminMenuItem.tsx';
import { canAccessAdminPanel } from '../../public/permissions.ts';
import { useKeycloak } from '../../shared/auth/KeycloakProvider.tsx';

export type CompaniesData =
  GetCompaniesForCurrentUserQuery['companiesForCurrentUser'];

interface UserCompaniesAndPrivilegeProviderProps {
  children: React.ReactNode;
}

export type CompaniesForUserContextType = {
  companies: CompaniesData | undefined;
  menu: IAdminMenuItem[];
  setMenu: React.Dispatch<React.SetStateAction<IAdminMenuItem[]>>;
  refetchCompanies: UseQueryExecute;
};

/**
 * This Provider watches all location changes in the Admin panel and checks whether the user has the right privilege to access a certain page
 * It also stores the current menu items and their state after applying the proper privileges and the currently selected item.
 * It also stores the currently used publicCompany and its information, as well as all accessible other companies and their domains (for the company switcher)
 */
export const CompaniesProvider: React.FC<
  UserCompaniesAndPrivilegeProviderProps
> = ({ children }) => {
  const location = useLocation({ select: (l) => l.pathname });
  const navigate = useNavigate();
  const [publicCompany] = usePublicCompany();
  const [companies, setCompanies] = useState<CompaniesData>();
  const { login } = useKeycloak();

  // The menu is a static data structure, but we need to apply privileges to it (so the "unallowed" items are disabled) and mark the currently active menu item
  const [privilegedCurrentMenu, setPrivilegedCurrentMenu] =
    useState<IAdminMenuItem[]>(menu);

  const [companiesQueryResult, refetchCompanies] = useQuery({
    query: CompaniesQuery,
  });

  useEffect(() => {
    if (
      companiesQueryResult.error &&
      companiesQueryResult.error.graphQLErrors.some(
        (gqlError) => gqlError.message === 'Forbidden resource',
      )
    ) {
      void login().catch((err) => console.error('[Auth] Login failed:', err));
    } else if (companiesQueryResult.data) {
      const currentCompany =
        companiesQueryResult.data.companiesForCurrentUser.find(
          (companyForUser) => companyForUser.id === publicCompany?.id,
        );
      if (currentCompany?.privileges?.length) {
        // Redirect to public frontend if the given user has none of the admin-role privileges
        const isAdminForCompany = canAccessAdminPanel(
          currentCompany?.privileges,
        );
        if (!isAdminForCompany) {
          window.location.href = `/`;
        }
      }
      // Cached deep-cloned snapshot for downstream consumers.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCompanies(companiesQueryResult.data.companiesForCurrentUser);
    }
  }, [
    companiesQueryResult.data,
    companiesQueryResult.error,
    login,
    publicCompany?.id,
  ]);

  useLayoutEffect(() => {
    if (companies && publicCompany) {
      // Find the currently used company among all companies that the user has access to
      const currentCompany = companies.find(
        (companyForUser) => companyForUser.id === publicCompany?.id,
      );

      let updatedMenu: IAdminMenuItem[] = [];

      // User has privilege, he may see the page but we need to prepare the menu to look correct
      if (currentCompany?.privileges) {
        // Update Menu to show currently active item and disable unallowed items
        updatedMenu = getPrivilegedCurrentMenu(
          currentCompany?.privileges,
          location,
        );
        // Menu shape depends on async privileges + current location.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPrivilegedCurrentMenu(updatedMenu);
      }

      const wantedMenuItem = findInMenuTree(
        (item) => item.href === location,
        updatedMenu,
      );

      // Do something if user has no privilege (e.g. go to error page)
      if (wantedMenuItem?.disabled) {
        const allowedAlternative = getFirstAllowedMenuItem(
          privilegedCurrentMenu,
        );

        if (allowedAlternative?.href)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          navigate({ to: allowedAlternative.href as any });
      }
    }
  }, [location, companies, publicCompany, navigate]);

  return (
    <CompaniesContext.Provider
      value={{
        companies,
        menu: privilegedCurrentMenu,
        setMenu: setPrivilegedCurrentMenu,
        refetchCompanies,
      }}
    >
      {children}
    </CompaniesContext.Provider>
  );
};

export const CompaniesContext = createContext<CompaniesForUserContextType>({
  companies: undefined,
  menu,
  setMenu: () => {},
  refetchCompanies: () => {},
});
